import { Box } from '@material-ui/core';
import { AllowedMimeType, BorrowerDocumentation, File } from '@nemuru-eng/js-api-client';
import React, { useEffect, useState } from 'react';

import { LazyElement } from '@/components/shared/classes/LazyElement';
import { CustomPaper } from '@/components/shared/components/CustomPaper';

import { useCustomTheme } from '../../../hooks/useCustomTheme';
import { NonAvailableElementWarning } from '../../modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/components/NonAvailableElementWarning';

interface DocumentVisualizerProps {
  id: string;
  documentFile: BorrowerDocumentation | File | undefined | null;
  loading?: boolean;
  onLoad?: () => void;
  isImage?: boolean;
  isFullWidth?: boolean;
  containerStyle?: React.CSSProperties;
}

export const DocumentVisualizer = ({
  id,
  documentFile,
  loading = false,
  onLoad,
  isImage = false,
  isFullWidth = false,
  containerStyle,
}: DocumentVisualizerProps) => {
  const { customBorderRadius, shadows } = useCustomTheme();

  const [loaded, setLoaded] = useState(false);

  const shadow = shadows[1] === 'none' ? '0 0 0 1px #eae9e6' : shadows[1];

  const [title] = useState(id as string);

  useEffect(() => {
    if (!documentFile || loaded) return;

    // Mount Lazy Element
    const isPdfDocument = documentFile.mimeType === AllowedMimeType.APPLICATION_PDF;

    const base64File = isPdfDocument
      ? `data:${documentFile.mimeType};base64,${documentFile.data}#toolbar=0`
      : `data:${documentFile.mimeType};base64,${documentFile.data}`;

    const lazyElement = new LazyElement(base64File, title);
    lazyElement.onLoad = onLoad;

    if (isPdfDocument) {
      lazyElement.mount('iframe', { height: '100%', width: '100%', border: 'none' });
    } else {
      lazyElement.mount('img', { maxHeight: isFullWidth ? 'auto' : '100%', maxWidth: '100%' });
    }

    setLoaded(true);

    return () => {
      lazyElement.unmount();
      setLoaded(false);
    };
  }, [documentFile]);

  return (
    <CustomPaper
      fill="grey.light"
      elevation={0}
      style={{ padding: 0, height: '250px', overflow: 'hidden', boxShadow: shadow }}
    >
      <Box
        id={title}
        style={{
          textAlign: 'center',
          height: '250px',
          borderRadius: customBorderRadius.paper,
          overflow: isFullWidth ? 'auto' : 'hidden',
          ...containerStyle,
        }}
      >
        {loading && (
          <NonAvailableElementWarning
            style={{ fontSize: 20 }}
            iconName={'schedule'}
            textId={isImage ? 'LOADING_IMAGE' : 'LOADING_DOCUMENT'}
          />
        )}
        {((!loading && !documentFile) || documentFile === null) && (
          <NonAvailableElementWarning
            iconName={isImage ? 'badge' : 'folder'}
            className={'appear-anim'}
            textId={isImage ? 'NO_IMAGE' : 'NO_DOCUMENT'}
          />
        )}
      </Box>
    </CustomPaper>
  );
};
